import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LogRocket from 'logrocket';

import { App } from './App';
import { appConfig } from './config/appConfig';
import { scrubHeaders } from './config/privacy';
import { AuthProvider } from './auth/AuthProvider';
import { ThemeProvider } from './theme/ThemeContext';
import './styles/global.css';

/* LogRocket privacy controls (PLA-627 / PLA-801) ---------------------------
 * Client-side redaction is the primary control for LogRocket: PHI must not
 * reach the vendor (SEC-199, T&C Sec 3). Provider identity (the signed-in
 * user's name, email, NPI) is NOT PHI: it is shown in replay and used as the
 * session identity (src/config/logrocketIdentity.ts). Patient-bearing
 * content is redacted via `data-private`; the sanitizers below cover the
 * network capture lane. Shared scrub helpers live in src/config/privacy.ts.
 */

// Initialise LogRocket (session replay / monitoring) as early as possible so
// it captures the full session from startup. See PLA-627.
LogRocket.init('umzy8z/kai-app', {
  // Environment label (ENV_NAME / VITE_ENV_NAME), "default" when unset. Lets us
  // separate local/staging/production sessions in the LogRocket dashboard.
  release: appConfig.envName,
  dom: {
    // Mask every input/textarea/select value by default (PLA-801). There are
    // no free-text inputs today, but this holds once a chat surface ships —
    // typed text could contain patient details. Opt individual non-sensitive
    // controls back in with `data-public` if replay debugging needs them.
    inputSanitizer: true,
  },
  network: {
    // Default-deny for network capture (PLA-801): request/response bodies are
    // dropped entirely because dashboard endpoints can carry PHI. URLs are
    // kept (the provider NPI in `provider=` is not PHI) and credential
    // headers are masked, so sessions keep their debugging value (endpoint,
    // method, status, and timing all remain visible).
    requestSanitizer: (request) => {
      scrubHeaders(request.headers);
      request.body = undefined;
      return request;
    },
    responseSanitizer: (response) => {
      scrubHeaders(response.headers);
      response.body = undefined;
      return response;
    },
  },
});

/**
 * React Query client — module-scoped so it survives StrictMode double-mount
 * and so devtools can attach to a stable instance.
 *
 * Defaults tuned for an internal dashboard:
 *  - `staleTime: 30s` so flipping between Lens domains doesn't refetch.
 *  - Single retry; the Kai-API surfaces meaningful errors we want to show.
 *  - No refetchOnWindowFocus — too aggressive for clinical staff who tab
 *    between charting tools.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  </StrictMode>,
);
